import type { ReactNode } from "react";

import {
  Box,
  type BoxProps,
  type CssProperties,
  HStack,
  VStack,
} from "@chakra-ui/react";

import { Tooltip } from "@shared/ui/chakra/overlays/tooltip";
import { Typography } from "@shared/ui/chakra/typography/typography";

type CellBodyType = "leading" | "trailing";

type Props = {
  body: ReactNode;
  bodyColor?: string;
  bodyFull?: string;
  caption?: string;
  captionColor?: string;
  captionDotColor?: string;
  captionFull?: string;
  captionWithDot?: boolean;
  icon?: ReactNode;
  iconAlign?: BoxProps["alignSelf"];
  lineClampBody?: number;
  overline?: ReactNode;
  type: CellBodyType;
  isSubtle?: boolean;
};

const withTooltip = (node: ReactNode, full?: string): ReactNode =>
  full ? <Tooltip content={full}>{node}</Tooltip> : node;

const bodyAlignMap: Record<
  CellBodyType,
  {
    alignItems: string;
    flex?: number;
    justify: CssProperties["alignItems"];
    order: number;
    textAlign: string;
    width: string;
    wordBreak: string;
  }
> = {
  leading: {
    alignItems: "flex-start",
    flex: 1,
    justify: "flex-start",
    order: 0,
    textAlign: "left",
    width: "full",
    wordBreak: "break-all",
  },
  trailing: {
    alignItems: "flex-end",
    justify: "flex-end",
    order: 1,
    textAlign: "right",
    width: "auto",
    wordBreak: "none",
  },
};

export const CellBody = ({
  body,
  bodyColor,
  bodyFull,
  caption,
  captionColor,
  captionDotColor,
  captionFull,
  captionWithDot = false,
  icon,
  iconAlign,
  lineClampBody = 1,
  overline,
  type,
  isSubtle,
}: Props) => {
  return (
    <HStack
      alignItems={"center"}
      flex={bodyAlignMap[type].flex}
      gap={"2.5"}
      justifyContent={bodyAlignMap[type].justify}
      w={bodyAlignMap[type].width}
      wordBreak={bodyAlignMap[type].wordBreak}
    >
      {icon && (
        <Box
          alignSelf={iconAlign}
          display="inline-flex"
          flexGrow={0}
          order={bodyAlignMap[type].order}
        >
          {icon}
        </Box>
      )}
      {(body || overline || caption) && (
        <VStack
          alignItems={bodyAlignMap[type].alignItems}
          gap={0}
          overflow="hidden"
          textAlign={bodyAlignMap[type].textAlign}
          width="full"
        >
          {typeof overline === "string" ? (
            <Typography
              color={isSubtle ? "text.fg_subtle" : "text.fg_muted"}
              textStyle="caption"
              whiteSpace="pre-line"
            >
              {overline}
            </Typography>
          ) : (
            overline
          )}
          {typeof body === "string"
            ? withTooltip(
                <Typography
                  color={isSubtle ? "text.fg_subtle" : bodyColor || "text.fg"}
                  lineClamp={lineClampBody}
                  textStyle="body"
                  whiteSpace="pre-line"
                >
                  {body}
                </Typography>,
                bodyFull,
              )
            : body}
          {caption &&
            withTooltip(
              <Typography
                alignItems="center"
                color={
                  isSubtle ? "text.fg_subtle" : captionColor || "text.fg_muted"
                }
                gap="4px"
                lineClamp="3"
                style={{
                  display: "flex",
                }}
                textStyle="body2"
                whiteSpace="pre-line"
              >
                {captionWithDot && (
                  <Box
                    bg={
                      isSubtle
                        ? "text.fg_subtle"
                        : captionDotColor || captionColor || "text.fg_muted"
                    }
                    h="6px"
                    rounded="6px"
                    w="6px"
                  />
                )}
                <span> {caption}</span>
              </Typography>,
              captionFull,
            )}
        </VStack>
      )}
    </HStack>
  );
};
